import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  AppState,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import {
  OTHER_CHOICE,
  SURVEY_MAX_COMMENT,
  SURVEY_MAX_OTHER,
  SURVEY_QUESTIONS,
  buildSurveyResponse,
  type SurveyAnswers,
} from '../utils/feedbackSurvey';
import { observedUpdatePatch, surveyDecision, type SurveyConfig, type SurveyState, type VersionCheck } from '../utils/optionalNudges';
import { checkInstalledVersion, isNativePlatform, patchNudgeState, submitSurveyResponse } from '../services/optionalNudges';

// The feedback survey (see optionalNudges.ts for every timing rule). Native:
// a version step first — the installed build is checked against the server's
// appConfig/update no matter which button is tapped; an outdated build
// pauses the survey (store link), and the survey only reopens 7 days after
// the app itself observes the updated build. Web goes straight to the five
// questions. Everyone gets the same thank-you, whatever they rated.

type Step =
  | 'blocked' | 'version' | 'checking' | 'outdated' | 'stillOld' | 'unknown' | 'updated'
  | 'q0' | 'q1' | 'q2' | 'q3' | 'rating' | 'thanks';

type Props = {
  visible: boolean;
  uid: string;
  config: SurveyConfig;
  survey: SurveyState | null;
  firstUse: number | null;
  manual: boolean;
  /** Dev builds only: walk through the real form without saving anything. */
  preview?: boolean;
  onClose: () => void;
};

const QUESTION_STEPS: Step[] = ['q0', 'q1', 'q2', 'q3', 'rating'];
const TOTAL = QUESTION_STEPS.length;

const FeedbackSurveyModal: React.FC<Props> = ({ visible, uid, config, survey, firstUse, preview = false, onClose }) => {
  // Preview never touches Firestore (dev-only entry point in Settings).
  const saveState = useMemo(() => (preview ? async (..._args: Parameters<typeof patchNudgeState>) => undefined : patchNudgeState), [preview]);
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const native = isNativePlatform();
  const surveyId = config?.surveyId || '';
  const [step, setStep] = useState<Step>('version');
  const [blocked, setBlocked] = useState<{ title: string; body: string } | null>(null);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [versionCheck, setVersionCheck] = useState<(VersionCheck & { versionName?: string; versionCode?: number }) | null>(null);
  const awaitingStoreReturn = useRef(false);
  const surveyRef = useRef(survey);
  surveyRef.current = survey;

  // Decide the opening step whenever the survey is (re)opened.
  useEffect(() => {
    if (!visible) return;
    setError(null);
    setAnswers({});
    setRating(null);
    setComment('');
    const decision = surveyDecision({ config, survey, firstUse, now: Date.now(), native, manual: true });
    const dateLabel = (ms?: number) => (ms ? new Date(ms).toLocaleDateString(language, { day: 'numeric', month: 'long' }) : '');
    if (decision.status === 'submitted') setBlocked({ title: t('feedbackSurvey.submittedTitle'), body: t('feedbackSurvey.submittedBody') });
    else if (decision.status === 'inactive') setBlocked({ title: t('feedbackSurvey.kicker'), body: t('feedbackSurvey.inactiveBody') });
    else if (decision.status === 'too_new') setBlocked({ title: t('feedbackSurvey.tooNewTitle'), body: t('feedbackSurvey.tooNewBody') });
    else if (decision.status === 'post_update_wait') setBlocked({ title: t('feedbackSurvey.waitTitle'), body: t('feedbackSurvey.waitBody').replace('{date}', dateLabel(decision.until)) });
    else setBlocked(null);
    const blockedNow = ['submitted', 'inactive', 'too_new', 'post_update_wait'].includes(decision.status);
    setStep(blockedNow ? 'blocked' : native ? 'version' : 'q0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const runVersionCheck = useCallback(async (afterStore: boolean) => {
    setStep('checking');
    const check = await checkInstalledVersion();
    setVersionCheck(check);
    const current = surveyRef.current;
    if (check.status === 'current') {
      const observed = observedUpdatePatch(current, check, Date.now());
      if (observed) {
        // The update happened since the survey paused: record it, reopen in 7 days.
        await saveState(uid, { surveys: { [surveyId]: observed } }).catch(() => undefined);
        setStep('updated');
        return;
      }
      setStep('q0');
      return;
    }
    if (check.status === 'outdated') {
      await saveState(uid, { surveys: { [surveyId]: { pendingUpdateTarget: check.target || 'unknown' } } }).catch(() => undefined);
      setStep(afterStore ? 'stillOld' : 'outdated');
      return;
    }
    setStep('unknown');
  }, [saveState, surveyId, uid]);

  // Back from the store: check the installed build again (never trust the tap).
  useEffect(() => {
    if (!visible) return undefined;
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active' && awaitingStoreReturn.current) {
        awaitingStoreReturn.current = false;
        void runVersionCheck(true);
      }
    });
    return () => subscription.remove();
  }, [runVersionCheck, visible]);

  const openStore = async () => {
    if (!versionCheck?.storeUrl) return;
    await saveState(uid, { surveys: { [surveyId]: { storeOpenedAt: Date.now() } } }).catch(() => undefined);
    awaitingStoreReturn.current = true;
    try {
      await Linking.openURL(versionCheck.storeUrl);
    } catch {
      awaitingStoreReturn.current = false;
    }
  };

  const submit = async () => {
    const built = buildSurveyResponse({
      surveyId,
      answers,
      rating,
      comment,
      platform: Platform.OS,
      appVersion: versionCheck?.versionName || null,
      appBuild: versionCheck?.versionCode ?? null,
    });
    if ('error' in built) {
      setError(built.error === 'rating_required' ? t('feedbackSurvey.ratingRequired') : t('feedbackSurvey.submitFailed'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (!preview) await submitSurveyResponse(uid, built.payload);
      setStep('thanks');
    } catch {
      setError(t('feedbackSurvey.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const questionIndex = QUESTION_STEPS.indexOf(step);
  const goBack = () => {
    if (questionIndex > 0) setStep(QUESTION_STEPS[questionIndex - 1]);
    else if (native) setStep('version');
  };
  const goNext = () => setStep(QUESTION_STEPS[Math.min(questionIndex + 1, TOTAL - 1)]);

  const renderInfo = (title: string, body: string, actions: React.ReactNode, icon = 'info') => (
    <View style={styles.info}>
      <View style={[styles.infoIcon, themed.iconWrap]}><Icon name={icon} size={22} color={theme.colors.accentSecondary} /></View>
      <Text style={[styles.title, styles.centered, themed.textPrimary]}>{title}</Text>
      <Text style={[styles.body, styles.centered, themed.textSecondary]}>{body}</Text>
      <View style={styles.actionsColumn}>{actions}</View>
    </View>
  );

  const button = (label: string, onPress: () => void, kind: 'primary' | 'secondary' = 'primary', disabled = false) => (
    <TouchableOpacity
      key={label}
      onPress={onPress}
      disabled={disabled}
      style={[styles.button, kind === 'primary' ? styles.buttonPrimary : themed.buttonSecondary, disabled && styles.disabled]}
      accessibilityRole="button"
    >
      <Text style={[styles.buttonText, kind === 'primary' ? styles.buttonPrimaryText : themed.textPrimary]}>{label}</Text>
    </TouchableOpacity>
  );

  const renderQuestion = (index: number) => {
    const question = SURVEY_QUESTIONS[index];
    const answer = answers[question.id];
    const setChoice = (choice: string) => setAnswers(current => ({
      ...current,
      [question.id]: current[question.id]?.choice === choice ? { choice: null } : { choice, other: current[question.id]?.other },
    }));
    return (
      <>
        <Text style={[styles.title, themed.textPrimary]}>{t(`feedbackSurvey.q.${question.id}.title`)}</Text>
        <View style={styles.options}>
          {[...question.options, OTHER_CHOICE].map(option => {
            const selected = answer?.choice === option;
            return (
              <TouchableOpacity
                key={option}
                onPress={() => setChoice(option)}
                style={[styles.option, themed.option, selected && themed.optionSelected]}
                accessibilityRole="radio"
                accessibilityState={{ checked: selected }}
              >
                <Icon name={selected ? 'check-circle' : 'circle'} size={18} color={selected ? theme.colors.accentSecondary : theme.colors.iconMuted} />
                <Text style={[styles.optionText, themed.textPrimary]}>
                  {option === OTHER_CHOICE ? t('feedbackSurvey.otherLabel') : t(`feedbackSurvey.q.${question.id}.${option}`)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
        {answer?.choice === OTHER_CHOICE ? (
          <TextInput
            value={answer.other || ''}
            onChangeText={text => setAnswers(current => ({ ...current, [question.id]: { choice: OTHER_CHOICE, other: text.slice(0, SURVEY_MAX_OTHER) } }))}
            placeholder={t('feedbackSurvey.otherPlaceholder')}
            placeholderTextColor={theme.colors.textMuted}
            maxLength={SURVEY_MAX_OTHER}
            style={[styles.input, themed.input]}
            accessibilityLabel={t('feedbackSurvey.otherPlaceholder')}
          />
        ) : null}
      </>
    );
  };

  const renderRating = () => (
    <>
      <Text style={[styles.title, themed.textPrimary]}>{t('feedbackSurvey.q.rating.title')}</Text>
      <View style={styles.stars} accessibilityRole="radiogroup">
        {[1, 2, 3, 4, 5].map(value => (
          <TouchableOpacity
            key={value}
            onPress={() => { setRating(value); setError(null); }}
            hitSlop={6}
            accessibilityRole="radio"
            accessibilityState={{ checked: rating === value }}
            accessibilityLabel={t('feedbackSurvey.q.rating.star').replace('{count}', String(value))}
          >
            <Icon name="star" size={36} color={rating !== null && value <= rating ? '#F5A524' : theme.colors.iconMuted} style={rating !== null && value <= rating ? styles.starFilled : undefined} />
          </TouchableOpacity>
        ))}
      </View>
      <Text style={[styles.label, themed.textSecondary]}>{t('feedbackSurvey.q.rating.commentLabel')}</Text>
      <TextInput
        value={comment}
        onChangeText={text => setComment(text.slice(0, SURVEY_MAX_COMMENT))}
        placeholder={t('feedbackSurvey.q.rating.commentPlaceholder')}
        placeholderTextColor={theme.colors.textMuted}
        maxLength={SURVEY_MAX_COMMENT}
        multiline
        style={[styles.input, styles.textarea, themed.input]}
        accessibilityLabel={t('feedbackSurvey.q.rating.commentLabel')}
      />
      <Text style={[styles.counter, themed.textMuted]}>{comment.length}/{SURVEY_MAX_COMMENT}</Text>
    </>
  );

  let content: React.ReactNode = null;
  if (step === 'blocked' && blocked) {
    content = renderInfo(blocked.title, blocked.body, button(t('feedbackSurvey.close'), onClose), 'clock');
  } else if (step === 'version') {
    content = renderInfo(t('feedbackSurvey.version.question'), t('feedbackSurvey.version.hint'), [
      button(t('feedbackSurvey.version.check'), () => void runVersionCheck(false)),
      button(t('feedbackSurvey.version.already'), () => void runVersionCheck(false), 'secondary'),
    ], 'download');
  } else if (step === 'checking') {
    content = (
      <View style={styles.info}>
        <ActivityIndicator color={theme.colors.accentSecondary} />
        <Text style={[styles.body, themed.textSecondary]}>{t('feedbackSurvey.version.checking')}</Text>
      </View>
    );
  } else if (step === 'outdated' || step === 'stillOld') {
    const version = versionCheck?.targetLabel || versionCheck?.target || '';
    content = renderInfo(
      step === 'outdated' ? t('feedbackSurvey.version.outdatedTitle') : t('feedbackSurvey.version.stillOldTitle'),
      step === 'outdated' ? t('feedbackSurvey.version.outdatedBody').replace('{version}', version) : t('feedbackSurvey.version.stillOldBody'),
      [button(t('feedbackSurvey.version.openStore'), () => void openStore()), button(t('feedbackSurvey.close'), onClose, 'secondary')],
      'arrow-up-circle',
    );
  } else if (step === 'unknown') {
    content = renderInfo(t('feedbackSurvey.version.unknownTitle'), t('feedbackSurvey.version.unknownBody'), [
      button(t('feedbackSurvey.version.retry'), () => void runVersionCheck(false)),
      button(t('feedbackSurvey.close'), onClose, 'secondary'),
    ], 'wifi-off');
  } else if (step === 'updated') {
    content = renderInfo(t('feedbackSurvey.version.updatedTitle'), t('feedbackSurvey.version.updatedBody'), button(t('feedbackSurvey.close'), onClose), 'check-circle');
  } else if (step === 'thanks') {
    content = renderInfo(t('feedbackSurvey.thanksTitle'), t('feedbackSurvey.thanksBody'), button(t('feedbackSurvey.done'), onClose), 'heart');
  } else if (questionIndex >= 0) {
    const answered = step === 'rating' ? rating !== null : Boolean(answers[SURVEY_QUESTIONS[questionIndex]?.id]?.choice);
    content = (
      <>
        <View style={styles.progressRow}>
          <Text style={[styles.kicker, themed.textMuted]}>{t('feedbackSurvey.progress').replace('{step}', String(questionIndex + 1)).replace('{total}', String(TOTAL))}</Text>
        </View>
        <View style={[styles.progressTrack, themed.progressTrack]} accessibilityRole="progressbar" accessibilityValue={{ min: 0, max: TOTAL, now: questionIndex + 1 }}>
          <View style={[styles.progressFill, { width: `${((questionIndex + 1) / TOTAL) * 100}%` }]} />
        </View>
        {step === 'rating' ? renderRating() : renderQuestion(questionIndex)}
        {error ? <Text style={styles.error} accessibilityLiveRegion="polite">{error}</Text> : null}
        <View style={styles.navRow}>
          {questionIndex > 0 || native ? (
            <TouchableOpacity onPress={goBack} style={[styles.navButton, themed.buttonSecondary]} accessibilityRole="button">
              <Icon name="arrow-left" size={16} color={theme.colors.icon} />
              <Text style={[styles.buttonText, themed.textPrimary]}>{t('feedbackSurvey.back')}</Text>
            </TouchableOpacity>
          ) : <View />}
          {step === 'rating' ? (
            <TouchableOpacity onPress={() => void submit()} disabled={submitting} style={[styles.navButton, styles.buttonPrimary, (!answered || submitting) && styles.disabled]} accessibilityRole="button">
              {submitting ? <ActivityIndicator color="#fff" /> : null}
              <Text style={[styles.buttonText, styles.buttonPrimaryText]}>{submitting ? t('feedbackSurvey.submitting') : t('feedbackSurvey.submit')}</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity onPress={goNext} style={[styles.navButton, answered ? styles.buttonPrimary : themed.buttonSecondary]} accessibilityRole="button">
              <Text style={[styles.buttonText, answered ? styles.buttonPrimaryText : themed.textPrimary]}>{answered ? t('feedbackSurvey.next') : t('feedbackSurvey.skip')}</Text>
              <Icon name="arrow-right" size={16} color={answered ? '#fff' : theme.colors.icon} />
            </TouchableOpacity>
          )}
        </View>
      </>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.root}>
        <Pressable style={[StyleSheet.absoluteFill, styles.backdrop]} onPress={step === 'thanks' || step === 'blocked' ? onClose : undefined} />
        <View style={[styles.sheet, themed.sheet, { paddingBottom: insets.bottom + 18 }]}>
          <View style={styles.header}>
            <Text style={[styles.kicker, themed.accent]}>{t('feedbackSurvey.kicker')}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={10} accessibilityRole="button" accessibilityLabel={t('feedbackSurvey.close')}>
              <Icon name="x" size={20} color={theme.colors.iconMuted} />
            </TouchableOpacity>
          </View>
          <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={styles.content}>{content}</ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, justifyContent: 'flex-end' },
  backdrop: { backgroundColor: 'rgba(0,0,0,0.55)' },
  sheet: { maxHeight: '92%', borderTopLeftRadius: 26, borderTopRightRadius: 26, borderWidth: 1, paddingTop: 16, paddingHorizontal: 20 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  kicker: { fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.4, textTransform: 'uppercase' },
  content: { gap: 14, paddingBottom: 8 },
  info: { alignItems: 'center', gap: 12, paddingVertical: 10 },
  infoIcon: { width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 20, lineHeight: 26, fontWeight: Typography.extrabold, textAlign: 'left', alignSelf: 'stretch' },
  body: { fontSize: 15, lineHeight: 22, alignSelf: 'stretch' },
  centered: { textAlign: 'center' },
  actionsColumn: { alignSelf: 'stretch', gap: 10, marginTop: 6 },
  button: { borderRadius: 16, paddingVertical: 14, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'transparent' },
  buttonPrimary: { backgroundColor: '#F27D26', borderColor: '#F27D26' },
  buttonText: { fontSize: 15, fontWeight: Typography.extrabold },
  buttonPrimaryText: { color: '#fff' },
  disabled: { opacity: 0.5 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between' },
  progressTrack: { height: 6, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: 6, borderRadius: 3, backgroundColor: '#F27D26' },
  options: { gap: 8 },
  option: { flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 14, borderWidth: 1, paddingVertical: 13, paddingHorizontal: 14 },
  optionText: { flex: 1, fontSize: 15, fontWeight: Typography.semibold },
  input: { borderRadius: 14, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15 },
  textarea: { minHeight: 96, textAlignVertical: 'top' },
  counter: { fontSize: 11, textAlign: 'right', marginTop: -8 },
  label: { fontSize: 14, fontWeight: Typography.semibold },
  stars: { flexDirection: 'row', justifyContent: 'center', gap: 10, paddingVertical: 6 },
  starFilled: { textShadowColor: 'rgba(245,165,36,0.35)', textShadowRadius: 8 },
  error: { color: '#EF4444', fontSize: 13 },
  navRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 10, marginTop: 4 },
  navButton: { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 16, paddingVertical: 13, paddingHorizontal: 18, borderWidth: 1, borderColor: 'transparent' },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  sheet: { backgroundColor: theme.isDark ? '#111826' : theme.colors.surfaceElevated, borderColor: theme.colors.border },
  textPrimary: { color: theme.colors.textPrimary },
  textSecondary: { color: theme.colors.textSecondary },
  textMuted: { color: theme.colors.textMuted },
  accent: { color: theme.colors.accentSecondary },
  iconWrap: { backgroundColor: theme.colors.accentSecondarySoft },
  buttonSecondary: { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)', borderColor: theme.colors.border },
  option: { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', borderColor: theme.colors.border },
  optionSelected: { borderColor: theme.colors.accentSecondary, backgroundColor: theme.colors.accentSecondarySoft },
  input: { color: theme.colors.textPrimary, borderColor: theme.colors.border, backgroundColor: theme.isDark ? 'rgba(255,255,255,0.04)' : '#fff' },
  progressTrack: { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
});

export default FeedbackSurveyModal;
